import { toast } from 'sonner'
import { ApiError } from './api'

/**
 * Texto próprio da tela para um erro, por `ApiError.code` ou por status.
 * O código ganha do status: `{ 409: 'Alguém mexeu', 'slot-taken': 'Horário
 * tomado' }` usa o segundo quando o código é esse.
 */
export type MensagensPorErro = Partial<Record<number | string, string>>

/** Frases que o servidor manda quando não sabe explicar melhor. */
const GENERICAS = new Set([
  'não deu para completar a operação.',
  'o pedido não pôde ser atendido.',
  'pedido inválido',
  'dados inválidos',
  'dados inválidos.',
  'não encontrado',
  'sem permissão',
  'algo quebrou aqui dentro',
  'algo falhou no servidor.',
  'o servidor está indisponível agora.',
  'o servidor respondeu fora do padrão.',
])

/** Códigos que não dizem nada além do status. */
const CODIGOS_GENERICOS = new Set(['erro', 'erro-interno', 'indisponivel', 'resposta-invalida'])

/** Mensagem padrão do zod e do Fastify, que sai em inglês. */
const INGLES =
  /\b(invalid|expected|required|received|must|too (big|small)|not found|cannot|undefined|failed)\b/i

function especifica(message: string): boolean {
  const texto = message.trim()
  return texto !== '' && !GENERICAS.has(texto.toLowerCase()) && !INGLES.test(texto)
}

/** O servidor escreve em minúscula e sem ponto; a tela mostra frase. */
function frase(texto: string): string {
  const limpo = texto.trim()
  const inicial = limpo.charAt(0).toUpperCase() + limpo.slice(1)
  return /[.!?]$/.test(inicial) ? inicial : `${inicial}.`
}

function segundosParaTentar(error: ApiError): number | null {
  const raw = error.details.retryAfterSeconds
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.ceil(raw) : null
}

function esperar(segundos: number | null): string {
  if (segundos === null) return 'Espere um pouco e tente de novo.'
  if (segundos < 90) return `Espere ${segundos} segundos e tente de novo.`
  return `Espere ${Math.ceil(segundos / 60)} minutos e tente de novo.`
}

function chaveTrancada(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 423 || error.code === 'chave-trancada'
  return error instanceof Error && error.message === 'a chave está trancada'
}

export function ehErroDeRede(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0
}

export function ehConflito(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409
}

export function ehNaoEncontrado(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

const FALHA_DA_TELA = 'Algo quebrou nesta tela. Recarregue a página e tente de novo.'

/**
 * O que aconteceu e o que fazer, numa ou duas frases.
 *
 * A frase do servidor ganha quando é específica ("esse horário já está
 * ocupado"); a genérica e a em inglês dão lugar ao texto do status.
 */
export function mensagemDeErro(error: unknown, contexto?: MensagensPorErro): string {
  if (error instanceof ApiError && contexto !== undefined) {
    const propria = contexto[error.code] ?? contexto[error.status]
    if (propria !== undefined) return propria
  }

  if (chaveTrancada(error)) {
    return 'A chave está trancada. Destrave com a senha da chave para continuar.'
  }

  if (!(error instanceof ApiError)) {
    // `Error` simples é o que o nosso código lança de propósito, em português.
    // TypeError e afins são falha de programação e não servem para a pessoa.
    if (error instanceof Error && error.name === 'Error' && especifica(error.message)) {
      return frase(error.message)
    }
    return FALHA_DA_TELA
  }

  const { status } = error
  if (status === 0) {
    if (error.code === 'offline') {
      return 'Sem conexão com a internet. Confira o Wi-Fi ou os dados móveis e tente de novo.'
    }
    if (error.code === 'tempo-esgotado') {
      return 'O servidor demorou demais para responder. Tente de novo em instantes.'
    }
    return 'Não foi possível falar com o servidor. Tente de novo em instantes.'
  }
  if (status === 401) return 'Sua sessão expirou. Entre de novo para continuar.'
  if (status === 429) return `Muitas tentativas seguidas. ${esperar(segundosParaTentar(error))}`

  if (status < 500 && error.code !== 'resposta-invalida' && especifica(error.message)) {
    return frase(error.message)
  }
  // 502 e 503 com código próprio explicam de propósito o que falta, como
  // integração desligada ou provedor que recusou. O 500 genérico nunca.
  if (
    (status === 502 || status === 503) &&
    !CODIGOS_GENERICOS.has(error.code) &&
    especifica(error.message)
  ) {
    return frase(error.message)
  }

  switch (status) {
    case 400:
    case 422:
      return 'Algum dado não foi aceito. Confira o que foi preenchido e tente de novo.'
    case 403:
      return 'Esta conta não tem permissão para isso.'
    case 404:
      return 'Não encontrado. Talvez tenha sido apagado. Volte e abra de novo.'
    case 409:
      return 'Isso mudou enquanto a tela estava aberta. Recarregue para ver a versão atual.'
    case 413:
      return 'O conteúdo é grande demais. Tente um arquivo ou texto menor.'
    case 502:
    case 503:
    case 504:
      return 'O servidor está indisponível agora. Tente de novo em alguns minutos.'
    default:
      if (error.code === 'resposta-invalida') {
        return 'O servidor respondeu fora do padrão. Recarregue a página e tente de novo.'
      }
      if (status >= 500) {
        return 'Algo falhou no servidor. O erro já foi registrado. Tente de novo em instantes.'
      }
      return 'Não deu para completar. Tente de novo.'
  }
}

/** Título curto para cabeçalho de erro e de aviso. */
export function tituloDeErro(error: unknown): string {
  if (chaveTrancada(error)) return 'Chave trancada'
  if (!(error instanceof ApiError)) return 'Algo deu errado'
  const { status } = error
  if (status === 0) return error.code === 'tempo-esgotado' ? 'Sem resposta' : 'Sem conexão'
  if (status === 401) return 'Sessão expirada'
  if (status === 403) return 'Sem permissão'
  if (status === 404) return 'Não encontrado'
  if (status === 409) return 'Conflito'
  if (status === 429) return 'Muitas tentativas'
  if (status === 400 || status === 422) return 'Dados não aceitos'
  if (status >= 500) return 'Erro no servidor'
  return 'Algo deu errado'
}

export interface ToastErroOpcoes {
  acao?: { label: string; onClick: () => void } | undefined
}

/**
 * Aviso de erro. O 401 não avisa: a tela já está indo para a entrada, e o
 * aviso sumiria no meio da troca.
 */
export function toastErro(
  error: unknown,
  contexto?: MensagensPorErro,
  opcoes?: ToastErroOpcoes,
): void {
  if (error instanceof ApiError && error.status === 401) return
  if (error instanceof DOMException && error.name === 'AbortError') return
  const acao = opcoes?.acao
  toast.error(tituloDeErro(error), {
    description: mensagemDeErro(error, contexto),
    // Erro precisa de tempo para ser lido; o padrão do sonner é 4 s.
    duration: 8000,
    ...(acao === undefined ? {} : { action: { label: acao.label, onClick: acao.onClick } }),
  })
}
